import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  CreateWishlistRequest,
  CreateWishlistSuccessResponse,
  AddWishlistItemSuccessResponse,
  DeleteAllWishlistItemsSuccessResponse,
  DeleteWishlistSuccessResponse,
  GetAllWishlistsSuccessResponse,
  DeleteWishlistRequest,
  AddWishlistItemRequest,
  DeleteAllWishlistItemsRequest,
  GetAllProductIdsByUserSuccessResponse,
  GetWishlistRequest,
  GetWishlistSuccessResponse,
  UpdateWishlistRequest,
  UpdateWishlistSuccessResponse,
} from "@b2bportal/travel-wishlists-api";
import { WISHLISTS_API_PREFIX } from "../constants/urlPaths";

export const wishlistsApi = createApi({
  reducerPath: "wishlists",
  baseQuery: fetchBaseQuery({ baseUrl: WISHLISTS_API_PREFIX }),
  tagTypes: ["Wishlist", "Wishlists", "AllWishlistIds"],
  endpoints: (build) => ({
    getWishlistById: build.query<
      GetWishlistSuccessResponse,
      GetWishlistRequest
    >({
      query(req) {
        return {
          url: "wishlist/get",
          method: "POST",
          body: req,
        };
      },
      providesTags: ["Wishlist"],
    }),
    getAllWishlists: build.query<GetAllWishlistsSuccessResponse, void>({
      query() {
        return {
          url: "wishlist/get-all",
          method: "POST",
        };
      },
      providesTags: ["Wishlists"],
    }),
    getAllProductIdsByUser: build.query<Set<string>, void>({
      query(req) {
        return {
          url: "wishlist/get-all-product-ids-by-user",
          method: "POST",
          body: req,
        };
      },
      transformResponse: (response: GetAllProductIdsByUserSuccessResponse) => {
        return new Set(response?.productIds?.map((id) => id.value));
      },
      providesTags: ["AllWishlistIds"],
    }),
    createWishlist: build.mutation<
      CreateWishlistSuccessResponse,
      CreateWishlistRequest
    >({
      query(req) {
        return {
          url: "wishlist/create",
          method: "POST",
          body: req,
        };
      },
      invalidatesTags: ["Wishlists"],
    }),
    deleteWishlist: build.mutation<
      DeleteWishlistSuccessResponse,
      DeleteWishlistRequest
    >({
      query(req) {
        return {
          url: "wishlist/delete",
          method: "POST",
          body: req,
        };
      },
      invalidatesTags: ["Wishlists", "AllWishlistIds"],
    }),
    addWishlistItem: build.mutation<
      AddWishlistItemSuccessResponse,
      AddWishlistItemRequest
    >({
      query(req) {
        return {
          url: "wishlist-item/create",
          method: "POST",
          body: req,
        };
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;

        // Manually update the RTK query cache to avoid another network call to refresh the items
        dispatch(
          wishlistsApi.util.updateQueryData(
            "getAllProductIdsByUser",
            undefined,
            (draft) => {
              draft.add(arg.product.id.value);
            }
          )
        );
      },
      invalidatesTags: ["Wishlists"],
    }),
    deleteAllWishlistItems: build.mutation<
      DeleteAllWishlistItemsSuccessResponse,
      DeleteAllWishlistItemsRequest
    >({
      query(req) {
        return {
          url: "wishlist-item/delete-all",
          method: "POST",
          body: req,
        };
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;

        // Manually update the RTK query cache to avoid another network call to refresh the items
        dispatch(
          wishlistsApi.util.updateQueryData(
            "getAllProductIdsByUser",
            undefined,
            (draft) => {
              draft.delete(arg.productId.value);
            }
          )
        );
      },
      invalidatesTags: ["Wishlists"],
    }),
    updateWishlist: build.mutation<
      UpdateWishlistSuccessResponse,
      UpdateWishlistRequest
    >({
      query(req) {
        return {
          url: "wishlist/update",
          method: "PUT",
          body: req,
        };
      },
      invalidatesTags: ["Wishlist"],
    }),
  }),
});

export const {
  useGetWishlistByIdQuery,
  useGetAllWishlistsQuery,
  useGetAllProductIdsByUserQuery,
  useCreateWishlistMutation,
  useDeleteWishlistMutation,
  useAddWishlistItemMutation,
  useDeleteAllWishlistItemsMutation,
  useUpdateWishlistMutation,
} = wishlistsApi;
